/* eslint-disable no-case-declarations */
import { reduce, get, isEqual, cloneDeep } from 'lodash';

import { serializeFilters } from '../../utils/filters';
import {
  isDisabledField,
  isDisabledWithoutPivots,
  createRowTemplate
} from '../../utils/report-builder';

export const sortByFieldType = (a, b) => a.fieldType - b.fieldType;

export const createFieldGroups = (fields, groups = {}) =>
  reduce(
    fields,
    (result, value) => {
      const key = value.view_label;
      if (!result[key]) {
        // eslint-disable-next-line no-param-reassign
        result[key] = {
          name: value.view_label,
          label: value.view_label,
          fields: [value],
          active: false,
          open: get(groups, [key, 'open'], true)
        };
      } else {
        result[key].fields.push(value);
      }

      return result;
    },
    {}
  );

const addDisabledFields = (fields, pivots = []) => {
  if (!fields || !Object.keys(fields).length) {
    return {};
  }

  Object.keys(fields).forEach(key => {
    // eslint-disable-next-line no-param-reassign
    fields[key].disabled =
      isDisabledField(fields, key) || isDisabledWithoutPivots(pivots, key);
  });

  return fields;
};

export const currentStateRendered = (state, stateFilters) => {
  const { pivots, values, generatedReport } = state;

  return (
    isEqual(pivots, generatedReport.pivots) &&
    isEqual(values, generatedReport.values) &&
    isEqual(
      serializeFilters(stateFilters.fields),
      serializeFilters(generatedReport.filters)
    )
  );
};

export const initialState = () => ({
  values: [],
  pivots: [],
  config: {},
  tableColumns: [],
  generatedReport: {
    values: [],
    pivots: [],
    filters: []
  },
  currentStateRendered: true,
  showTable: false,
  triggerFetch: false
});

export function reducer(state, action) {
  const { values, pivots, config } = state;
  const { view, filters, toggledField, isPivot, name, savedReport } = action;
  const fields = get(view, 'fields', {});
  const groups = get(config, 'groups', []);
  let newFields;

  switch (action.type) {
    case 'TOGGLE_FIELD':
      const [pivotExists] = pivots.filter(f => f.name === toggledField.name);
      const [valueExists] = values.filter(f => f.name === toggledField.name);

      if (pivotExists) {
        pivots.splice(pivots.indexOf(pivotExists), 1);
        toggledField.active = false;
        toggledField.active = false;
        if (isPivot) {
          values.push(toggledField);
          values.sort(sortByFieldType);
          toggledField.active = true;
          toggledField.activePivot = false;
        }
      } else if (valueExists) {
        values.splice(values.indexOf(valueExists), 1);
        toggledField.active = false;
        toggledField.activePivot = false;
        if (isPivot) {
          pivots.push(toggledField);
          toggledField.active = true;
          toggledField.activePivot = true;
        }
      } else if (!isPivot) {
        values.push(toggledField);
        values.sort(sortByFieldType);
        toggledField.active = true;
        toggledField.activePivot = false;
      } else {
        pivots.push(toggledField);
        toggledField.active = true;
        toggledField.activePivot = true;
      }
      config.fields[toggledField.name] = toggledField;

      return {
        ...state,
        values,
        pivots,
        currentStateRendered: currentStateRendered(
          { ...state, values, pivots },
          action.filters
        ),
        config: { ...config, fields: addDisabledFields(config.fields, pivots) }
      };
    case 'REORDER_LIST':
      return { ...state, [action.listName]: action.list };
    case 'TOGGLE_GROUP':
      groups[name].open = !groups[name].open;

      return { ...state, config: { ...state.config, groups } };
    case 'FETCHED_FIELDS':
      const currentState = config.fields;
      newFields = cloneDeep(fields);

      if (newFields && Object.keys(newFields).length) {
        Object.keys(newFields).forEach(key => {
          // eslint-disable-next-line no-param-reassign
          if (currentState && currentState[key]) {
            newFields[key].active = currentState[key].active;
            newFields[key].activePivot = currentState[key].activePivot;
          }
          newFields[key].disabled = isDisabledWithoutPivots([], key);
        });
      }

      return {
        ...state,
        config: { fields: newFields, groups: createFieldGroups(newFields) }
      };
    case 'REPORT_GENERATED':
      return {
        ...state,
        generatedReport: {
          values: [...values],
          pivots: [...pivots],
          filters: { ...filters }
        },
        showTable: true,
        currentStateRendered: true
      };
    case 'DATA_UPDATED':
      const [row] = action.data;

      if (!row || (action.data.length && row.error)) {
        return { ...state, tableColumns: [] };
      }

      return {
        ...state,
        tableColumns: createRowTemplate(
          action.data,
          [...pivots, ...values],
          pivots
        )
      };
    case 'CHECK_CURRENT_STATE':
      return {
        ...state,
        currentStateRendered: currentStateRendered(
          { ...state, values, pivots },
          filters
        )
      };
    case 'LOAD_SAVED_REPORT':
      newFields = cloneDeep(config.fields);
      const newValues = [];
      const newPivots = [];

      const hasValue = k =>
        savedReport.values && savedReport.values.includes(k);
      const hasPivot = k =>
        savedReport.pivots && savedReport.pivots.includes(k);

      if (newFields && Object.keys(newFields).length) {
        Object.keys(newFields).forEach(key => {
          // eslint-disable-next-line no-param-reassign
          if (hasValue(key) && !hasPivot(key)) {
            newFields[key].active = true;
            newValues.push(newFields[key]);
          } else if (hasPivot(key)) {
            newFields[key].active = true;
            newFields[key].activePivot = true;
            newPivots.push(newFields[key]);
          } else if (
            key === 'pdt_character_vote_unified.actor_name' &&
            hasValue('character_vote.actor_name') &&
            !hasPivot('character_vote.actor_name')
          ) {
            newFields[key].active = true;
            newValues.push(newFields[key]);
          } else if (
            key === 'pdt_character_vote_unified.character_name' &&
            hasValue('character_vote.character_name') &&
            !hasPivot('character_vote.character_name')
          ) {
            newFields[key].active = true;
            newValues.push(newFields[key]);
          } else if (
            key === 'client_crosstab.title' &&
            hasValue('client_crosstab.tv_show_name') &&
            !hasPivot('client_crosstab.tv_show_name')
          ) {
            newFields[key].active = true;
            newValues.push(newFields[key]);
          } else if (
            key === 'client_crosstab.first_release_date' &&
            hasValue('client_crosstab.tv_show_first_air_date') &&
            !hasPivot('client_crosstab.tv_show_first_air_date')
          ) {
            newFields[key].active = true;
            newValues.push(newFields[key]);
          } else if (
            key === 'pdt_genre_unified.genre' &&
            hasValue('tv_show_genre.genre') &&
            !hasPivot('tv_show_genre.genre')
          ) {
            newFields[key].active = true;
            newValues.push(newFields[key]);
          } else if (
            key === 'pdt_character_vote_unified.actor_name' &&
            hasPivot('character_vote.actor_name')
          ) {
            newFields[key].active = true;
            newFields[key].activePivot = true;
            newPivots.push(newFields[key]);
          } else if (
            key === 'pdt_character_vote_unified.character_name' &&
            hasPivot('character_vote.character_name')
          ) {
            newFields[key].active = true;
            newFields[key].activePivot = true;
            newPivots.push(newFields[key]);
          } else if (
            key === 'client_crosstab.title' &&
            hasPivot('client_crosstab.tv_show_name')
          ) {
            newFields[key].active = true;
            newFields[key].activePivot = true;
            newPivots.push(newFields[key]);
          } else if (
            key === 'client_crosstab.first_release_date' &&
            hasPivot('client_crosstab.tv_show_first_air_date')
          ) {
            newFields[key].active = true;
            newFields[key].activePivot = true;
            newPivots.push(newFields[key]);
          } else if (
            key === 'pdt_genre_unified.genre' &&
            hasPivot('tv_show_genre.genre')
          ) {
            newFields[key].active = true;
            newFields[key].activePivot = true;
            newPivots.push(newFields[key]);
          } else {
            newFields[key].active = false;
          }
          newFields[key].disabled = isDisabledWithoutPivots(
            savedReport.pivots,
            key
          );
        });
      }

      return {
        ...state,
        values: newValues,
        pivots: newPivots,
        generatedReport: {
          values: [...newValues],
          pivots: [...newPivots],
          filters: [...savedReport.filters]
        },
        config: {
          ...config,
          fields: newFields
        },
        currentStateRendered: true,
        showTable: true,
        triggerFetch: true
      };
    case 'CLEAR_REPORT':
      return initialState();
    default:
      throw new Error();
  }
}

export default {
  initialState,
  reducer,
  createFieldGroups,
  sortByFieldType
};
